import { useState } from 'react';
import { NJButton, NJCheckbox, NJFormItem, NJHeading, NJInlineMessage, NJText } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { territoryCatalogue, testTerritoryUrl } from './testTerritory';

interface Props {
  title: string;
  initialCodes: string[];
  error: string | null;
}

/** Préparation de l'entretien, séparée des écrans produit et des droits du projet. */
export function TestTerritorySetup({ title, initialCodes, error }: Props) {
  const [selected, setSelected] = useState(initialCodes);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const entries = territoryCatalogue.filter(c => selected.includes(c.insee));
  const bytes = entries.reduce((sum, c) => sum + c.bytes, 0);
  const url = selected.length ? testTerritoryUrl(selected) : '';
  const toggle = (code: string, checked: boolean) => {
    setSelected(current => checked ? [...new Set([...current, code])] : current.filter(value => value !== code));
    setCopyState('idle');
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch (cause) {
      console.error('[plan-navigation-v3] Impossible de copier le lien du test', cause);
      setCopyState('error');
    }
  };
  return (
    <PrototypeStudioLayout title={title}>
      <main style={{
        maxWidth: '60rem', margin: '0 auto', padding: 'var(--nj-semantic-size-spacing-32)',
        display: 'flex', flexDirection: 'column', gap: 'var(--nj-semantic-size-spacing-24)',
      }}>
        <header>
          <NJText scale="sm">Préparation du test · Animateur</NJText>
          <NJHeading as="h1" scale="lg">Sur quel territoire tester Plan ?</NJHeading>
          <NJText>
            Choisissez une ou plusieurs communes. Seuls leurs packs de données moquées seront chargés.
            Le participant choisira ensuite lui-même le périmètre de son projet.
          </NJText>
        </header>
        {error && <NJInlineMessage variant="error" role="alert">{error}</NJInlineMessage>}
        <fieldset onKeyDownCapture={event => {
          // Fluid 3.0.0-next.8 toggles Space internally without notifying onChange.
          const target = event.target;
          if (event.key !== ' ' || !(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
          const code = target.id.replace('test-territory-', '');
          if (!territoryCatalogue.some(c => c.insee === code)) return;
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat) toggle(code, !selected.includes(code));
        }} style={{
          margin: 0, border: '1px solid var(--nj-semantic-color-border-primary-default)',
          padding: 'var(--nj-semantic-size-spacing-24)', display: 'flex', flexDirection: 'column',
          gap: 'var(--nj-semantic-size-spacing-12)',
        }}>
          <legend><NJText as="span">Communes disponibles pour le test</NJText></legend>
          {territoryCatalogue.map(c => (
            <NJCheckbox
              key={c.insee}
              inputId={`test-territory-${c.insee}`}
              name="territoires"
              value={c.insee}
              label={`${c.name} (${c.insee})`}
              isChecked={selected.includes(c.insee)}
              onChange={(_, checked) => toggle(c.insee, !!checked)}
            />
          ))}
        </fieldset>
        <NJText role="status" aria-live="polite">
          {selected.length
            ? `${selected.length} commune${selected.length > 1 ? 's' : ''} · ${(bytes / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} Mo à charger`
            : 'Sélectionnez au moins une commune pour lancer le test.'}
        </NJText>
        <NJFormItem
          id="test-territory-link"
          label="Lien pour relancer ce test"
          value={url}
          readOnly
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--nj-semantic-size-spacing-12)' }}>
          <NJButton label="Lancer le test" disabled={!selected.length} onClick={() => window.location.assign(url)} />
          <NJButton label="Copier le lien" variant="secondary" disabled={!selected.length} onClick={() => { void copy(); }} />
        </div>
        {copyState === 'copied' && <NJText role="status">Lien copié.</NJText>}
        {copyState === 'error' && <NJInlineMessage variant="error" role="alert">Copie impossible. Copiez le lien directement dans le champ ci-dessus.</NJInlineMessage>}
        <NJText scale="sm">Cette préparation ne fait pas partie de l’application Plan. Le lien ouvre directement le prototype, sans ce réglage.</NJText>
      </main>
    </PrototypeStudioLayout>
  );
}
